import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import { UserGlobalContextMemorySpace } from "../../../contexts/user/userGlobalContext";
import { NavLink, useLocation } from "react-router-dom";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { Row, Col } from "react-bootstrap";
import "./style.scss";
import { useCredential } from "../../../hooks/useCredential";
import { formatDate } from '../../../utils/format_date'
import { calculateAge } from '../../../utils/calculate_old'
import FaUserCircle from "../../../assets/img/hb/Perfil Menú.svg";
import MisTramitesIcon from "../../../assets/img/hb/MisTramitesMenu.svg";
import IconPlanDeSalud from "../../../assets/img/iconPlanDeSalud.svg";

// PBI-25040 (rework): mapeo ruta -> sección del acordeón. Se usa para que el
// cierre automático solo aplique al cambiar a OTRO ítem del menú, no al
// navegar entre hijos de la misma sección ya abierta. Las rutas
// /debito-automatico y /debito-cuenta están ocultas del menú (ver más abajo)
// pero se incluyen igual para que Facturación no quede en estado inconsistente
// si se llega a ellas por otra vía (deep link, notificación, etc).
const PERFIL_ROUTES = ["/perfil", "/cambiar-contrasena"];
const MI_SALUD_ROUTES = ["/recetas", "/epicrisis", "/ordenes-medicas", "/notas-medicas"];
const MIS_TRAMITES_ROUTES = ["/adjuntar-documentacion", "/mis-autorizaciones", "/certificado-asistencia"];
const FACTURACION_ROUTES = ["/facturas", "/debito-automatico", "/debito-cuenta", "/historial-debito"];
const REINTEGRO_ROUTES = ["/reintegro-estado", "/reintegro-solicitud"];
const PLAN_DE_SALUD_ROUTES = [...FACTURACION_ROUTES, ...REINTEGRO_ROUTES, "/credencial-digital"];

const UserSidebar = () => {
  const { userPSHB, user, hasAutorizaciones } = useContext(UserGlobalContextMemorySpace);
  const [isActivePerfil, setIsActivePerfil] = useState(false);
  const [isActiveReintegro, setIsActiveReintegro] = useState(false);
  const [isActiveFacturacion, setIsActiveFacturacion] = useState(false);
  const [isActiveMiSalud, setIsActiveMiSalud] = useState(false)
  const [isActiveMisTramites, setIsActiveMisTramites] = useState(false);
  const [isActivePlanDeSalud, setIsActivePlanDeSalud] = useState(false)
  const planDeSaludRef = useRef(null);
  const location = useLocation();

  // PBI-25040 (rework): el cierre automático debe aplicar solo al cambiar a
  // OTRO ítem del menú lateral, no al navegar entre hijos de una sección ya
  // abierta. Si la sección ya estaba abierta y la nueva ruta le pertenece, se
  // mantiene abierta; en cualquier otro caso se cierra. No se fuerza apertura
  // por deep link (arranca cerrado si el usuario no tocó el menú).
  useEffect(() => {
    const path = location.pathname;
    setIsActivePerfil((prev) => prev && PERFIL_ROUTES.includes(path));
    setIsActiveMiSalud((prev) => prev && MI_SALUD_ROUTES.includes(path));
    setIsActiveMisTramites((prev) => prev && MIS_TRAMITES_ROUTES.includes(path));
    setIsActivePlanDeSalud((prev) => prev && PLAN_DE_SALUD_ROUTES.includes(path));
    setIsActiveFacturacion((prev) => prev && FACTURACION_ROUTES.includes(path));
    setIsActiveReintegro((prev) => prev && REINTEGRO_ROUTES.includes(path));
  }, [location.pathname]);

  const handleMenuClick = (e) => {
    if (
      isActivePlanDeSalud &&
      planDeSaludRef.current &&
      !planDeSaludRef.current.contains(e.target)
    ) {
      setIsActivePlanDeSalud(false);
      setIsActiveFacturacion(false);
      setIsActiveReintegro(false);
    }
  };

  const handleToggleFacturacion = () => {
    setIsActiveFacturacion((prev) => {
      const next = !prev;
      if (next) setIsActiveReintegro(false);
      return next;
    });
  };

  const handleToggleReintegro = () => {
    setIsActiveReintegro((prev) => {
      const next = !prev;
      if (next) setIsActiveFacturacion(false);
      return next;
    });
  };

  const handleTogglePerfil = () => {
    setIsActivePerfil((prev) => {
      const next = !prev;
      if (next) {
        setIsActiveMiSalud(false);
        setIsActivePlanDeSalud(false);
        setIsActiveFacturacion(false);
        setIsActiveReintegro(false);
        setIsActiveMisTramites(false);
      }
      return next;
    });
  };

  const handleToggleMiSalud = () => {
    setIsActiveMiSalud((prev) => {
      const next = !prev;
      if (next) {
        setIsActivePerfil(false);
        setIsActivePlanDeSalud(false);
        setIsActiveFacturacion(false);
        setIsActiveReintegro(false);
        setIsActiveMisTramites(false);
      }
      return next;
    });
  };

  const handleTogglePlanDeSalud = () => {
    setIsActivePlanDeSalud((prev) => {
      const next = !prev;
      if (next) {
        setIsActivePerfil(false);
        setIsActiveMiSalud(false);
        setIsActiveMisTramites(false);
      } else {
        setIsActiveFacturacion(false);
        setIsActiveReintegro(false);
      }
      return next;
    });
  };

  // PBI-25040 (rework): "Mis Trámites" quedaba fuera del grupo de exclusión
  // mutua de los acordeones de primer nivel (Perfil, Mi Salud, Plan de Salud).
  // Se agrega el mismo patrón que los demás: al abrir, cierra el resto.
  const handleToggleMisTramites = () => {
    setIsActiveMisTramites((prev) => {
      const next = !prev;
      if (next) {
        setIsActivePerfil(false);
        setIsActiveMiSalud(false);
        setIsActivePlanDeSalud(false);
        setIsActiveFacturacion(false);
        setIsActiveReintegro(false);
      }
      return next;
    });
  };

  const { user: {
    data: { Customers: {
      firstName,
      lastName,
      socialSecurityNumber,
      email,
      socialSecurityPlan,
      dni,
      birthday,
      socialSecurity
    } },
  } } = user

  const { getCredential, loadCredential, credencial } = useCredential(dni)

  const ivaInfo = useMemo(() => {
    if (!credencial || !credencial.IvaTax) return null;
    const infoTax = credencial.IvaTax === 'N' ? 'EXENTO' : 'GRAVADO';
    return infoTax;
  }, [credencial?.IvaTax]);

  const truncateEmail = (email) => {
    if (!email) return '';
    return email.length > 23 ? email.substring(0, 23) + '...' : email;
  };

  const handleRedirect = () => {
    window.open("https://www.hospitalbritanico.org.ar/", "_blank");
  };

  useEffect(() => {
    getCredential()
  }, []);

  return (
    <>
      <aside
        className={
          userPSHB
            ? "asideWebPSHB d-flex justify-content-center scrollable-aside"
            : "asideWebHB d-flex justify-content-center scrollable-aside"
        }
      >
        <div className="d-flex flex-column" style={{ width: "100%" }}>
          <div className="d-flex align-items-center" style={{ backgroundColor: "#efefef", paddingBottom: "25px" }}>
            {/* <span
              className="material-symbols-outlined"
              style={{
                fontSize: "40px",
                marginRight: "14px",
                marginLeft: "30px",
                color: "#224A8D"
              }}
            >account_circle</span> */}
              <img
              src={FaUserCircle}
              alt="..."
              style={{
                height: "40px",
                width: "40px",
                marginRight: "14px",
                marginLeft: "30px",
              }}
            />
            <div>
              <div
                style={{
                  color: "#224A8D",
                  fontFamily: "Roboto-Bold",
                  marginRight: "10px",
                  fontSize: "17px",
                }}
              >
                {firstName}
                <br />
                {lastName}
              </div>
              <div className="text_old_years">
                {calculateAge(birthday)}
              </div>
            </div>
          </div>
          <ul className="items_card_container">
            <li>E-mail: <span>{truncateEmail(email)}</span></li>
            <li>Plan: <span>{socialSecurity && socialSecurity !== 'PARTICULAR' ? socialSecurityPlan : '- -- SIN PLAN --'}</span></li>
            <li>N° afiliado: <span>{socialSecurityNumber}</span></li>
            {credencial && socialSecurity === '40080/000' && <li>IVA: <span>{loadCredential ? 'Verificando Plan...' : ivaInfo}</span></li>}
            {credencial && socialSecurity === '40080/000' && <li>Fecha de ingreso: <span>{loadCredential ? 'Verificando Plan...' : formatDate({ date: credencial.FechaIngreso, view_time: false })}</span></li>}
          </ul>
          <div className="separadorTitulo" style={{ width: "100%" }} />
          <div
            className="d-flex flex-column justify-content-center"
            style={{ width: "100%", backgroundColor: "#efefef" }}
            onClick={handleMenuClick}
          >
            <NavLink
              className="navigate-button"
              to={"/"}
              activeclassname="active"
            >
              <div className="d-flex textoMenuLateral">
                <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>home</span>{" "}
                <span>Inicio</span>
              </div>
            </NavLink>
            <div className="separadorItem" />
            <Row style={{ width: "100%", marginLeft: 0 }}>
              <NavLink
                className="navigate-button"
                to="/perfil"
                activeClassName="active"
                style={{ width: "78%" }}
              >
                <Col md={10}>
                  <div className="d-flex textoMenuLateral">
                    <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>account_circle</span>{" "}
                    <span>Perfil</span>
                  </div>
                </Col>
              </NavLink>
              <Col md={2} style={{ margin: "auto", marginLeft: "7px" }}>
                {isActivePerfil ? (
                  <FiChevronUp
                    size={20}
                    onClick={handleTogglePerfil}
                  />
                ) : (
                  <FiChevronDown
                    size={20}
                    onClick={handleTogglePerfil}
                  />
                )}
              </Col>
            </Row>

            {isActivePerfil ? (
              <NavLink
                className="navigate-button"
                to={"/cambiar-contrasena"}
                activeclassname="active"
              >
                <div className="d-flex textoMenuLateral">
                  <span style={{ paddingLeft: "30px" }}>
                    Cambiar contraseña
                  </span>
                </div>
              </NavLink>
            ) : null}
            <div className="separadorItem" />
            <NavLink
              className="navigate-button"
              to="/grupo-familiar"
              activeclassname="active"
            >
              <div className="d-flex textoMenuLateral">
                <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>diversity_4</span>{" "}
                <span>Grupo familiar</span>
              </div>
            </NavLink>
            <div className="separadorItem" />

            <NavLink
              className="navigate-button"
              to="/resultados"
              activeclassname="active"
            >
              <div className="d-flex textoMenuLateral">
                <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>inventory</span>{" "}
                <span>Resultados</span>
              </div>
            </NavLink>
            <div className="separadorItem" />
            <NavLink
              className="navigate-button"
              to="/turnos"
              activeclassname="active"
            >
              <div className="d-flex textoMenuLateral">
                <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>calendar_today</span>{" "}
                <span>Turnos</span>
              </div>
            </NavLink>
            <div className="separadorItem" />
            <Row
              style={{ marginLeft: 0 }}
              onClick={(e) => {
                if (
                  e.target.tagName.toLowerCase() === "svg" ||
                  e.target.tagName.toLowerCase() === "polyline"
                ) {
                  e.stopPropagation();
                }
              }}
            >
              <div
                className="navigate-button perfil-bottom"
                activeclassname="active"
                style={{ width: "72%" }}
                onClick={(e) => {
                  if (
                    e.target.tagName.toLowerCase() === "svg" ||
                    e.target.tagName.toLowerCase() === "polyline"
                  ) {
                    e.stopPropagation();
                  }
                  handleToggleMiSalud();
                }}
              >
                <Col md={10}>
                  <div className="d-flex textoMenuLateral">
                    <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>assignment</span>{" "}
                    <span>Mi Salud</span>
                  </div>
                </Col>
              </div>
              <Col style={{ margin: "auto", marginLeft: "10px" }}>
                {isActiveMiSalud ? (
                  <FiChevronUp
                    size={20}
                    onClick={handleToggleMiSalud}
                  />
                ) : (
                  <FiChevronDown
                    size={20}
                    onClick={handleToggleMiSalud}
                  />
                )}
              </Col>
            </Row>
            {isActiveMiSalud ? (
              <>
                <NavLink
                  className="navigate-button"
                  to={"/recetas"}
                  activeclassname="active"
                >
                  <div className="d-flex textoMenuLateral">
                    <span style={{ paddingLeft: "30px" }}>Recetas</span>
                  </div>
                </NavLink>
                <NavLink
                  className="navigate-button"
                  to={"/epicrisis"}
                  activeclassname="active"
                >
                  <div className="d-flex textoMenuLateral">
                    <span style={{ paddingLeft: "30px" }}>Epicrisis</span>
                  </div>
                </NavLink>
                <NavLink
                  className="navigate-button"
                  to={"/ordenes-medicas"}
                  activeclassname="active"
                >
                  <div className="d-flex textoMenuLateral">
                    <span style={{ paddingLeft: "30px" }}>Ordenes clínicas</span>
                  </div>
                </NavLink>
                <NavLink
                  className="navigate-button"
                  to={"/notas-medicas"}
                  activeclassname="active"
                >
                  <div className="d-flex textoMenuLateral">
                    <span style={{ paddingLeft: "30px" }}>Notas Medicas</span>
                  </div>
                </NavLink>
              </>
            ) : null}
            <div className="separadorItem" />
            {/* <NavLink
              className="navigate-button"
              to="/recetas"
              activeclassname="active"
            >
              <div className="d-flex textoMenuLateral">
                <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>prescription</span>{" "}
                <span>Recetas</span>
              </div>
            </NavLink> */}
            <Row
              style={{ marginLeft: 0 }}
              onClick={(e) => {
                if (
                  e.target.tagName.toLowerCase() === "svg" ||
                  e.target.tagName.toLowerCase() === "polyline"
                ) {
                  e.stopPropagation();
                }
              }}
            >
              <div
                className="navigate-button perfil-bottom"
                activeclassname="active"
                style={{ width: "72%" }}
                onClick={(e) => {
                  if (
                    e.target.tagName.toLowerCase() === "svg" ||
                    e.target.tagName.toLowerCase() === "polyline"
                  ) {
                    e.stopPropagation();
                  }
                  handleToggleMisTramites();
                }}
              >
                <Col md={10}>
                  <div className="d-flex textoMenuLateral">
                    <div>
                      <img src={MisTramitesIcon} className="iconosAtencion" alt="Mis trámites" style={{ width: "24px", height: "24px" }} />{" "}
                      <span>Mis trámites</span>
                    </div>
                  </div>
                </Col>
              </div>
              <Col style={{ margin: "auto", marginLeft: "10px" }}>
                {isActiveMisTramites ? (
                  <FiChevronUp
                    size={20}
                    onClick={handleToggleMisTramites}
                  />
                ) : (
                  <FiChevronDown
                    size={20}
                    onClick={handleToggleMisTramites}
                  />
                )}
              </Col>
            </Row>
            {isActiveMisTramites ? (
              <>
                <NavLink
                  className="navigate-button"
                  to="/adjuntar-documentacion"
                  activeclassname="active"
                >
                  <div className="d-flex textoMenuLateral">
                    <span style={{ paddingLeft: "30px" }}>Adjuntar documentación</span>
                  </div>
                </NavLink>
                {hasAutorizaciones && (
                  <NavLink
                    className="navigate-button"
                    to="/mis-autorizaciones"
                    activeclassname="active"
                  >
                    <div className="d-flex textoMenuLateral">
                      <span style={{ paddingLeft: "30px" }}>Mis autorizaciones</span>
                    </div>
                  </NavLink>
                )}
                <NavLink
                  className="navigate-button"
                  to="/certificado-asistencia"
                  activeclassname="active"
                >
                  <div className="d-flex textoMenuLateral">
                    <span style={{ paddingLeft: "30px" }}>Certificados</span>
                  </div>
                </NavLink>
              </>
            ) : null}
            <div className="separadorItem" />
            <a
              className="navigate-button"
              activeclassname="active"
              onClick={handleRedirect}
            >
              <div className="d-flex textoMenuLateral">
                <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>health_cross</span>{" "}
                <span>Hospital Británico</span>
              </div>
            </a>
            <div className="separadorItem" />
            <NavLink
              className="navigate-button"
              to="/atencion-al-paciente"
              activeclassname="active"
            >
              <div className="d-flex textoMenuLateral">
                <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>support_agent</span>{" "}
                <span>Atención al paciente</span>
              </div>
            </NavLink>
            <div className="separadorItem" />
            {/* <NavLink
              className="navigate-button"
              to={"/atencion-al-paciente"}
              activeclassname="active"
            >
              <div className="d-flex textoMenuLateral">
                <img
                  src={FaAtencion}
                  className="iconosAtencion"
                  alt="..."
                />{" "}
                <span>Atención al paciente</span>
              </div>
            </NavLink>
            <div className="separadorItem" /> */}
            <NavLink
              className="navigate-button"
              to="/micrositio"
              activeclassname="active"
            >
              <div className="d-flex textoMenuLateral">
                <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>help_outline</span>{" "}
                <span>Central de ayuda</span>
              </div>
            </NavLink>
            {/* <NavLink
              className="navigate-button"
              to={"/ayuda"}
              activeclassname="active"
            >
              <div className="d-flex textoMenuLateral">
                <img
                  src={FaAyuda}
                  className="iconosAtencion"
                  alt="..."
                />{" "}
                <span>Ayuda</span>
              </div>
            </NavLink> */}
            {user?.user?.data?.Customers?.pshb && (
              <div ref={planDeSaludRef}>
                <div className="separadorTitulo" style={{ width: "100%" }} />
                <Row
                  style={{ width: "100%", marginLeft: 0 }}
                  onClick={(e) => {
                    if (
                      e.target.tagName.toLowerCase() === "svg" ||
                      e.target.tagName.toLowerCase() === "polyline"
                    ) {
                      e.stopPropagation();
                    }
                    handleTogglePlanDeSalud();
                  }}
                >
                  <div
                    className="navigate-button perfil-bottom"
                    activeclassname="active"
                    style={{ width: "78%" }}
                  >
                    <Col md={10}>
                      <div className="d-flex textoMenuLateral">
                        <img
                          src={IconPlanDeSalud}
                          className="iconosAtencion"
                          alt="Plan de Salud"
                          style={{ width: "24px", height: "24px" }}
                        />{" "}
                        <span>Plan de Salud</span>
                      </div>
                    </Col>
                  </div>
                  <Col md={2} style={{ margin: "auto", marginLeft: "7px" }}>
                    {/* Sin onClick propio: el toggle ya lo maneja el onClick del Row
                        (bubbling), agregar uno aquí duplicaba el toggle y lo anulaba. */}
                    {isActivePlanDeSalud ? (
                      <FiChevronUp size={20} />
                    ) : (
                      <FiChevronDown size={20} />
                    )}
                  </Col>
                </Row>
                {isActivePlanDeSalud && (
                  <>
                <Row
                  style={{ width: "100%", marginLeft: 0 }}
                  onClick={(e) => {
                    if (
                      e.target.tagName.toLowerCase() === "svg" ||
                      e.target.tagName.toLowerCase() === "polyline"
                    ) {
                      e.stopPropagation();
                    }
                    handleToggleFacturacion();
                  }}
                >
                  <NavLink
                    className="navigate-button"
                    to={"/facturas"}
                    activeClassName="active"
                    style={{ width: "78%" }}
                  >
                    <div className="d-flex textoMenuLateral">
                      <span style={{ paddingLeft: "30px" }}>Facturación</span>
                    </div>
                  </NavLink>
                  <Col md={2} style={{ margin: "auto", marginLeft: "7px" }}>
                    {/* Sin onClick propio: el toggle ya lo maneja el onClick
                        del Row (bubbling); agregar uno aquí duplicaría el
                        toggle, igual que el fix ya aplicado al chevron de
                        "Plan de Salud". */}
                    {isActiveFacturacion ? (
                      <FiChevronUp size={20} />
                    ) : (
                      <FiChevronDown size={20} />
                    )}
                  </Col>
                </Row>
                {isActiveFacturacion ? (
                  <>
                    <NavLink
                      className="d-flex navigate-button navigate-button-submenu textoMenuLateral"
                      style={{
                        fontSize: "20px",
                        width: "100%",
                      }}
                      to={"/debito-automatico"}
                      activeclassname="active"
                    >
                      <span style={{ paddingLeft: "30px" }}>
                        En tarjeta de crédito
                      </span>
                    </NavLink>
                    <NavLink
                      className="d-flex navigate-button navigate-button-submenu textoMenuLateral"
                      style={{
                        fontSize: "20px",
                        width: "100%",
                      }}
                      to={"/debito-cuenta"}
                      activeclassname="active"
                    >
                      <span style={{ paddingLeft: "30px" }}>
                        En cuenta bancaria
                      </span>
                    </NavLink>
                    <NavLink
                      className="d-flex navigate-button navigate-button-submenu textoMenuLateral"
                      style={{
                        fontSize: "20px",
                        width: "100%",
                      }}
                      to={"/historial-debito"}
                      activeclassname="active"
                    >
                      <span style={{ paddingLeft: "30px" }}>Historial</span>
                    </NavLink>
                  </>
                ) : null}
                <div className="separadorItem" />
                <Row
                  style={{ width: "100%", marginLeft: 0 }}
                  onClick={(e) => {
                    if (
                      e.target.tagName.toLowerCase() === "svg" ||
                      e.target.tagName.toLowerCase() === "polyline"
                    ) {
                      e.stopPropagation();
                    }
                    handleToggleReintegro();
                  }}
                >
                  <div
                    className="navigate-button"
                    // to={"/perfil"}
                    activeclassname="active"
                    style={{ width: "78%" }}
                  >
                    <div className="d-flex textoMenuLateral">
                      <span style={{ paddingLeft: "30px" }}>Reintegros</span>
                    </div>
                  </div>
                  <Col md={2} style={{ margin: "auto", marginLeft: "7px" }}>
                    {/* Sin onClick propio: el toggle ya lo maneja el onClick
                        del Row (bubbling); agregar uno aquí duplicaría el
                        toggle. */}
                    {isActiveReintegro ? (
                      <FiChevronUp size={20} />
                    ) : (
                      <FiChevronDown size={20} />
                    )}
                  </Col>
                </Row>
                {isActiveReintegro ? (
                  <>
                    <NavLink
                      className="d-flex navigate-button navigate-button-submenu textoMenuLateral"
                      style={{
                        fontSize: "20px",
                        width: "100%",
                      }}
                      to={"/reintegro-estado"}
                      activeclassname="active"
                    >
                      <span style={{ paddingLeft: "30px" }}>Estado</span>
                    </NavLink>
                    <NavLink
                      className="d-flex navigate-button navigate-button-submenu textoMenuLateral"
                      style={{
                        fontSize: "20px",
                        width: "100%",
                      }}
                      to={"/reintegro-solicitud"}
                      activeclassname="active"
                    >
                      <span style={{ paddingLeft: "30px" }}>Solicitud</span>
                    </NavLink>
                  </>
                ) : null}
                <div className="separadorItem" />
                <Row style={{ width: "100%", marginLeft: 0 }}>
                  <NavLink
                    className="navigate-button"
                    to="/credencial-digital"
                    activeclassname="active"
                    style={{ width: "78%" }}
                  >
                    <div className="d-flex textoMenuLateral">
                      <span style={{ paddingLeft: "30px" }}>Credencial</span>
                    </div>
                  </NavLink>
                </Row>
                  </>
                )}
              </div>
            )}

            <div className="separadorTitulo" style={{ width: "100%" }} />
            <NavLink
              className="navigate-button"
              to="/cerrar-sesion"
              activeclassname="active"
            >
              <div className="d-flex textoMenuLateral">
                <span className="material-symbols-outlined iconosAtencion" style={{ fontSize: "24px" }}>logout</span>{" "}
                <span>Cerrar sesión</span>
              </div>
            </NavLink>
          </div>
        </div>
      </aside>
    </>
  );
};

export default UserSidebar;
